import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X, ChevronDown, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const NAV_LINKS = [
  { label: "About Us", path: "/about" },
  {
    label: "Directories",
    children: [
      { label: "Business Directory", path: "/directory" },
      { label: "Market Directory", path: "/markets" },
    ],
  },
  { label: "Upcoming Events", path: "/events" },
  { label: "Contact Us", path: "/contact" },
  { label: "Work With Us", path: "/work-with-us" },
];

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isAdmin = !!localStorage.getItem("bizkeep_admin_token");

  const dashboardPath = user?.userType === "market" ? "/market-dashboard" : user?.userType === "shopper" ? "/shopper-profile" : "/dashboard";

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    navigate("/");
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("bizkeep_admin_token");
    setMobileOpen(false);
    navigate("/admin");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActiveDir = location.pathname === "/directory" || location.pathname === "/markets";

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
          <span className="font-bold text-[#1a1a1a] text-lg tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>
            614 SHOP LOCAL
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <div key={link.label} className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`nav-link px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                    isActiveDir ? "text-[#1a1a1a] active" : "text-gray-600 hover:text-[#1a1a1a]"
                  }`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="nav-directories-dropdown"
                >
                  {link.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-50">
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setDropdownOpen(false)}
                        className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                          location.pathname === child.path
                            ? "text-[#1a1a1a] bg-gray-50"
                            : "text-gray-600 hover:text-[#1a1a1a] hover:bg-gray-50"
                        }`}
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                        data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-[#1a1a1a] active"
                    : "text-gray-600 hover:text-[#1a1a1a]"
                }`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        {/* Desktop Auth */}
        <div className="hidden lg:flex items-center gap-3">
          {isAdmin ? (
            <>
              <Link to="/admin/dashboard">
                <Button variant="ghost" className="text-sm font-medium text-gray-600 hover:text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="nav-admin-portal-btn">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Back to Admin Portal
                </Button>
              </Link>
              <Button
                onClick={handleAdminLogout}
                className="bg-[#1a1a1a] hover:bg-black text-white rounded-full px-6 py-2 text-sm font-bold transition-all hover:-translate-y-0.5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="nav-admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </>
          ) : isAuthenticated ? (
            <>
              <Link to={dashboardPath}>
                <Button variant="ghost" className="text-sm font-medium text-gray-600 hover:text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="nav-profile-btn">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  View Profile
                </Button>
              </Link>
              <Button
                onClick={handleLogout}
                className="bg-[#1a1a1a] hover:bg-black text-white rounded-full px-6 py-2 text-sm font-bold transition-all hover:-translate-y-0.5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-sm font-medium text-gray-600 hover:text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="nav-login-btn">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-6 py-2 text-sm font-bold transition-all hover:-translate-y-0.5" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="nav-join-btn">
                  Join 614 Shop Local
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button className="lg:hidden p-2 text-[#1a1a1a]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 mobile-menu-enter">
          <div className="px-6 py-4 space-y-1">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <p className="px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {link.label}
                  </p>
                  {link.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => setMobileOpen(false)}
                      className={`block px-3 py-3 pl-6 text-sm font-medium rounded-lg transition-colors ${
                        location.pathname === child.path
                          ? "bg-gray-50 text-[#1a1a1a]"
                          : "text-gray-600 hover:bg-gray-50 hover:text-[#1a1a1a]"
                      }`}
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === link.path
                      ? "bg-gray-50 text-[#1a1a1a]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-[#1a1a1a]"
                  }`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              {isAdmin ? (
                <>
                  <Link to="/admin/dashboard" onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" className="w-full rounded-full text-sm font-medium" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="mobile-nav-admin-portal-btn">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Back to Admin Portal
                    </Button>
                  </Link>
                  <Button onClick={handleAdminLogout} className="w-full bg-[#1a1a1a] hover:bg-black text-white rounded-full text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="mobile-nav-admin-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </Button>
                </>
              ) : isAuthenticated ? (
                <>
                  <Link to={dashboardPath} onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" className="w-full rounded-full text-sm font-medium" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="mobile-nav-profile-btn">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> View Profile
                    </Button>
                  </Link>
                  <Button onClick={handleLogout} className="w-full bg-[#1a1a1a] hover:bg-black text-white rounded-full text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="mobile-nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" className="w-full rounded-full text-sm font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>Login</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Join 614 Shop Local</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
